/**
 * ══ SỔ ĐĂNG KÝ GIAO DIỆN — MỘT NGUỒN CHO MENU, ROUTER, THANH TAB VÀ MÁY CHỦ (D03c §3.1) ═══════════
 * Cửa dữ liệu thuần như `routes.ts`: không Cedar, không `node:fs` ⇒ `apps/web` import được.
 *
 * Đợt 0 chỉ KHAI cây: chưa nút nào đổi quyền theo đây. Đợt 1 gắn `role_grant` (CSDL) và `can()` vào cây này.
 * Thêm tab mới vào một màn ⇒ thêm nút ở tệp nhóm tương ứng; `ui-tree.test.ts` bắt nút mồ côi, mã trùng,
 * màn thiếu đường dẫn.
 */
import type { UiNode } from './ui-tree/types.ts'
import { SALES_NODES } from './ui-tree/sales.ts'
import { OPS_NODES } from './ui-tree/ops.ts'
import { PURCHASE_DOCS_NODES } from './ui-tree/purchase-docs.ts'
import { ACCOUNTING_NODES } from './ui-tree/accounting.ts'
import { CATALOG_NODES } from './ui-tree/catalog.ts'
import { OTHER_NODES } from './ui-tree/other.ts'

export { PERMS, type Perm, type UiNode, type UiNodeKind } from './ui-tree/types.ts'

/** Cha luôn đứng trước con. Màn cấu hình dựng cây theo `parent`; màn ẩn khỏi menu nhận biết bằng cờ `hidden`. */
export const UI_TREE: readonly UiNode[] = [
  ...SALES_NODES, ...OPS_NODES, ...PURCHASE_DOCS_NODES, ...ACCOUNTING_NODES, ...CATALOG_NODES, ...OTHER_NODES,
]

const BY_ID = new Map(UI_TREE.map((n) => [n.id, n]))

export const uiNode = (id: string): UiNode | undefined => BY_ID.get(id)

export const uiChildren = (id: string): UiNode[] => UI_TREE.filter((n) => n.parent === id)

/** Tổ tiên từ gốc xuống, không gồm chính nút. Cây hỏng (cha không tồn tại) thì dừng ở chỗ đứt — test bắt chuyện đó. */
export const uiAncestors = (id: string): UiNode[] => {
  const out: UiNode[] = []
  let p = BY_ID.get(id)?.parent
  while (p !== undefined) {
    const n = BY_ID.get(p)
    if (n === undefined) break
    out.unshift(n)
    p = n.parent
  }
  return out
}

/** Màn ứng với một đường dẫn (khớp nguyên văn sau khi bỏ `?…` và `/` cuối). */
export const uiScreenOfPath = (pathname: string): UiNode | undefined => {
  const p = (pathname || '/').split('?')[0]?.replace(/\/+$/, '') || '/'
  return UI_TREE.find((n) => n.kind === 'screen' && n.path === p)
}

/** Các giá trị tab chọn được ở cấp ngay dưới `id` — đi xuyên qua nút nhóm (`group`). Dùng cho hook tab của web. */
export const uiTabValues = (id: string): string[] =>
  uiChildren(id).flatMap((n) => {
    if (n.kind !== 'tab') return []
    if (n.group === true) return uiTabValues(n.id)
    return n.tab === undefined ? [] : [n.tab]
  })
